import React from 'react';
import { Box, Flex, Spacer, Text} from '@chakra-ui/react'
import { grouper } from '../helpers/grouper';

// Modern design constants
const BORDER_RADIUS = {
  sm: 'var(--radius-md)',
  md: 'var(--radius-lg)',
  lg: 'var(--radius-xl)'
};

const CARD_VARIANTS = {
  default: {
    bg: 'var(--bg-elevated)',
    backdropFilter: 'blur(20px)',
    border: '1px solid var(--border-default)',
    boxShadow: 'var(--shadow-lg)',
    transition: 'all var(--transition-base)'
  },
  glass: {
    bg: 'var(--bg-glass)',
    backdropFilter: 'blur(20px)',
    border: '1px solid var(--border-subtle)',
    boxShadow: 'var(--shadow-glass)',
    transition: 'all var(--transition-base)'
  },
  elevated: {
    bg: 'var(--bg-elevated)',
    boxShadow: 'var(--shadow-xl)',
    border: '1px solid var(--border-default)',
    transition: 'all var(--transition-base)'
  }
};

export const CardWrapper = ({ children }) => {
  return (
    <Box>{ children }</Box>
  )
}

export const Left = ({ children }) => {
  return <Box order={1}>{ children }</Box>
}

export const Right = ({ children }) => {
  return <Box order={3}>{ children }</Box>
}

export const Spaced = ({leftKey, rightKey, children, ...props}) => {
  const groups = grouper(children);
  return (
    <Flex 
      direction={{ base: 'column', sm: 'column', md: 'row', lg: 'row' }}
      alignItems={{ base: 'stretch', md: 'center' }}
      gap={{ base: 2, sm: 2, md: 3 }}
      {...props}
    >
      { groups[leftKey] } 
      <Spacer order={2} display={{ base: 'none', md: 'block' }} /> 
      { groups[rightKey]}
    </Flex>
  )
}

export const CardAbove = ({ children }) => {
  return (
    <Spaced leftKey={Left.name} rightKey={Right.name}>{ children }</Spaced>
  )
}

export const CardBelow = ({ children }) => {
  return (
    <Spaced leftKey={Left.name} rightKey={Right.name}>{ children }</Spaced>
  )
}

export const Card = ({ 
  children, 
  variant = 'default',
  size = 'md',
  hover = true,
  animate = true,
  ...props 
}) => {
  const cardStyle = CARD_VARIANTS[variant] || CARD_VARIANTS.default;
  
  return (
    <Box 
      {...cardStyle}
      borderRadius={{ base: 'var(--radius-md)', sm: 'var(--radius-md)', md: BORDER_RADIUS[size] }}
      className={`${animate ? 'animate-fade-in' : ''} modern-card`}
      _hover={hover ? {
        transform: { base: 'none', md: 'translateY(-4px)' },
        boxShadow: variant === 'glass' ? 'var(--shadow-glass)' : 'var(--shadow-2xl)',
        ...cardStyle
      } : {}}
      position="relative"
      overflow="hidden"
      mx={{ base: 0, sm: 0 }}
      my={{ base: 2, md: 3 }}
      {...props}
    >
      {/* Subtle gradient overlay for depth */}
      <Box
        position="absolute"
        top={0}        left={0}
        right={0}
        height="1px"
        bg="linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)"
        zIndex={1}
      />
      { children }
    </Box>
  )
}

export const CardHeader = ({ 
  children, 
  isNaive = false, 
  variant = 'primary',
  ...props
}) => {
  const responsivePadding = isNaive ? 
    { px: { base: 2, md: 3 }, py: { base: 1.5, md: 2 } } : 
    { px: { base: 3, md: 6 }, py: { base: 3, md: 4 } };
  
  const headerVariants = {
    primary: {
      background: 'linear-gradient(135deg, #10b981, #059669)',
      color: 'white'
    },
    secondary: {
      background: 'linear-gradient(135deg, #ef4444, #dc2626)',
      color: 'white'
    },
    accent: {
      background: 'linear-gradient(135deg, #22c55e, #16a34a)',
      color: 'white'
    },
    glass: {
      background: 'var(--bg-glass)',
      backdropFilter: 'blur(10px)',
      border: '1px solid var(--border-subtle)',
      color: 'var(--text-default)'
    }
  };
  
  const headerStyle = headerVariants[variant] || headerVariants.primary;
  
  const finalProps = {
    ...headerStyle,
    ...responsivePadding,
    borderTopRadius: { base: 'var(--radius-md)', sm: 'var(--radius-md)', md: BORDER_RADIUS.md },
    position: 'relative',
    overflow: 'hidden',
    ...props
  };
  
  return (
    <Box {...finalProps}>
      {/* Subtle shine effect */}
      <Box
        position="absolute"
        top={0}
        left="-100%"
        width="100%"
        height="100%"
        background="linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)"
        className="animate-shimmer"
        zIndex={1}
      />
      <Spaced leftKey={CardTitle.name} rightKey={Right.name} position="relative" zIndex={2}>
        { children }
      </Spaced>
    </Box>
  )
}

export const CardTitle = ({ 
  isNaive = false, 
  size = 'md',
  children 
}) => {
  const responsiveSizeMap = {
    sm: { base: '0.75rem', md: '0.875rem' },
    md: { base: '0.875rem', md: '1rem' }, 
    lg: { base: '1rem', md: '1.125rem' },
    xl: { base: '1.125rem', md: '1.25rem' }
  };
  
  const props = {
    fontWeight: '600',
    fontSize: isNaive ? { base: '0.75rem', md: '0.875rem' } : responsiveSizeMap[size],
    color: 'inherit',
    letterSpacing: '-0.025em',
    lineHeight: '1.4',
    noOfLines: { base: 2, md: 1 }
  };
  
  return (
    <Text {...props}>
      { children }
    </Text>
  )
}

export const CardContent = ({ 
  children, 
  spacing = 'md',
  ...props 
}) => {
  const spacingMap = {
    sm: { base: 2, md: 3 },
    md: { base: 3, md: 6 },
    lg: { base: 4, md: 8 }
  };
  
  return (
    <Box 
      p={spacingMap[spacing]}
      position="relative"
      {...props}
    >
      { children }
    </Box>
  )
}

export const CardFooter = ({ 
  children, 
  variant = 'default',
  ...props 
}) => {
  const footerVariants = {
    default: {
      bg: 'var(--bg-subtle)',
      borderTop: '1px solid var(--border-default)'
    },
    glass: {
      bg: 'var(--bg-glass)',
      backdropFilter: 'blur(10px)',
      borderTop: '1px solid var(--border-subtle)'
    },
    accent: {
      bg: 'linear-gradient(135deg, #ecfdf5, #d1fae5)',
      borderTop: '1px solid #a7f3d0'
    }
  };
  
  const footerStyle = footerVariants[variant] || footerVariants.default;
  
  const finalProps = {
    ...footerStyle,
    p: { base: 3, md: 4 },
    borderBottomRadius: { base: 'var(--radius-md)', sm: 'var(--radius-md)', md: BORDER_RADIUS.md },
    ...props
  };
  
  return (
    <Spaced leftKey='Left' rightKey='Right' {...finalProps}>
      { children }
    </Spaced>
  )
}
